import React, { useEffect, useRef } from 'react';
import { Deal } from 'models/Deal';
import { Filters } from './filters';
import { useDeals } from './useDeals';
import { ColumnsType } from 'antd/es/table';
import { PERMISSIONS } from 'constants/permissions';
import { LoadingOutlined } from '@ant-design/icons';
import { HasAccess } from 'components/common/accessControl';
import { FilterIcon, ViewIcon } from 'components/common/icons';
import { DealStatusBadge } from 'components/common/StatusBadge';
import { Button, Col, Image, Input, PageTitle, Row, Table, Typography } from 'components';
import styles from './styles.module.scss';

const { Search } = Input;

interface DealsProps {
  forDashboard?: boolean;
  onTotalUpdate?: (total: number) => void;
}

export const Deals = React.memo(({ forDashboard, onTotalUpdate }: DealsProps) => {
  const {
    data,
    isLoading,
    routeChange,
    pagination,
    onSearch,
    closeDrawer,
    openDrawer,
    filterDrawerVisible,
    form,
    handleTableChange,
    applyFilters,
    resetFilters,
    totalElements,
  } = useDeals(forDashboard);

  // Track the previous total to avoid unnecessary updates
  const previousTotalRef = useRef<number | undefined>(undefined);

  // Report total count back to parent dashboard component
  useEffect(() => {
    if (forDashboard && onTotalUpdate && totalElements !== undefined && previousTotalRef.current !== totalElements) {
      // Update the ref to track the latest value
      previousTotalRef.current = totalElements;
      onTotalUpdate(totalElements);
    }
  }, [forDashboard, onTotalUpdate, totalElements]);

  const columns: ColumnsType<Deal> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (_, { uuid }) => {
        return uuid;
      },
    },
    {
      title: 'Client Name',
      dataIndex: 'clientName',
      key: 'client',
      width: 200,
      ellipsis: true,
      render: (value) => {
        return <Typography.Text ellipsis={{ tooltip: true }}>{value}</Typography.Text>;
      },
    },
    {
      title: 'Country',
      dataIndex: 'countryResponse',
      key: 'countryId',
      sorter: false,
      render: (country) => {
        return (
          <Row wrap={false} align={'middle'} className={styles['gap-12']}>
            {country?.fileLocation && (
              <Image
                src={country?.fileLocation}
                width={18}
                height={18}
                preview={false}
                style={{ verticalAlign: 'unset', width: '18px' }}
                className={styles['radius-circle']}
              />
            )}
            <Col>{country?.name}</Col>
          </Row>
        );
      },
    },
    {
      title: 'Created Date',
      dataIndex: 'formattedCreateDate',
      key: 'createDate',
      minWidth: 130,
      sorter: true,
      render: (value) => value,
    },
    {
      title: 'Last Updated',
      dataIndex: 'formattedLastModifiedDate',
      key: 'lastModifiedDate',
      minWidth: 130,
      sorter: true,
      render: (value) => value,
    },
    {
      title: 'Client Request Num',
      dataIndex: 'clientRequestNumber',
      key: 'clientRequestNum',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (status) => <DealStatusBadge type={status} />,
    },
    {
      fixed: 'right',
      width: '36px',
      key: 'actions',
      render: (value) => (
        <Button onClick={() => routeChange(value.id)} type={'text'} size={'small'} icon={<ViewIcon />} />
      ),
    },
  ];

  return (
    <Col span={24} className={styles.main}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Row align={'middle'} justify={'space-between'}>
            <PageTitle title={'Deals'} />
          </Row>
        </Col>
        <Col span={24} id={'deals_list'} className={styles.main_container}>
          <Row>
            <Col span={24}>
              <Row justify={'end'} className={styles.actions_container}>
                <Col style={{ width: '300px' }}>
                  <Search enterButton={false} placeholder={'Search'} onChange={(e) => onSearch(e.target.value)} />
                </Col>
                <Button color="default" variant="filled" onClick={openDrawer} icon={<FilterIcon />} />
              </Row>
            </Col>
            <Col span={24} style={{ paddingTop: '20px' }}>
              <HasAccess role={PERMISSIONS.deals.deal.read}>
                <Table
                  tableLayout={'auto'}
                  bordered
                  scroll={{
                    x: true,
                  }}
                  rowKey="id"
                  size={'small'}
                  dataSource={data}
                  columns={columns}
                  loading={{
                    indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
                    spinning: isLoading,
                  }}
                  locale={
                    isLoading
                      ? { emptyText: null } // Hide "No Data" text while loading
                      : {} // Use default AntD "No Data" icon when empty
                  }
                  onChange={handleTableChange}
                  pagination={{
                    position: ['bottomCenter'],
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} items`,
                    size: 'default',
                    current: pagination.page,
                    pageSize: pagination.size,
                    total: totalElements,
                  }}
                />
              </HasAccess>
            </Col>
          </Row>
        </Col>
      </Row>
      <Filters
        form={form}
        applyFilters={applyFilters}
        drawerOpen={filterDrawerVisible}
        closeDrawer={closeDrawer}
        resetFilters={resetFilters}
      />
    </Col>
  );
});

export default Deals;
